import debug from "debug";
import { exp } from "three/examples/jsm/nodes/Nodes.js";
const log = debug("app:dataprovider");
/**
 * Class to handle API requests with support for CRUD operations!
 * @category Experience
 * @subcategory Utils
 */
class DataProvider {
  /**
   * Initializes a new instance of the DataProvider.
   * @param {Object} experience - The experience instance containing the base URL.
   * @param {number} timeout - Timeout in milliseconds for the API request.
   * @param {number} limit - Max number of objects/results returned by the API request.
   */
  constructor(experience, timeout = 8000, limit = 10000) {
    this.experience = experience;
    this.eventHub = this.experience.eventHub;

    this.baseUrl = this.experience.config.baseApiUrl; // Access baseApiUrl from experience config

    log("Base API URL:", this.baseUrl);
    this.timeout = timeout;
    this.limit = limit;
  }

  cleanup() {
    log("DataProvider: Starting cleanup...");

    if (Array.isArray(this.someArray)) {
        this.someArray.forEach(item => {
            if (item && typeof item.cleanup === "function") {
                item.cleanup();
            }
        });
    } else {
        console.warn("DataProvider: someArray is undefined or not an array.");
    }

    // Now set it to null to free memory
    this.someArray = null;

    log("DataProvider cleanup complete.");
}
  /**
   * Fetches data from the API.
   * @param {string} endpoint - The API endpoint to fetch.
   * @param {string} method - HTTP method (GET, POST, PUT, DELETE).
   * @param {Object} data - Data to be sent with the request.
   * @param {number} customTimeout - Optional custom timeout in milliseconds (overrides default).
   * @returns {Promise<Object>} The JSON response from the API.
   */
  async fetch(endpoint, method = "GET", data = null, customTimeout = null) {
    const headers = await this._getAuthHeaders();
    const url = `${this.baseUrl}/${endpoint}`;

    const timeout = customTimeout !== null ? customTimeout : this.timeout;
    
    // Create abort controller and timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      log(`[DataProvider] ⏱️ TIMEOUT: Request aborted after ${timeout}ms for ${method} ${url}`);
      controller.abort();
    }, timeout);
    
    const config = {
      method,
      headers,
      signal: controller.signal,
    };

    if (data && (method === "POST" || method === "PUT")) {
      config.body = JSON.stringify(data);
      const bodySize = new Blob([config.body]).size;
      log(`[DataProvider] Request body size: ${(bodySize / 1024).toFixed(2)} KB`);
    }

    try {
      log(`[DataProvider] Fetching: ${method} ${url} (timeout: ${timeout}ms)`);
      if (method === "GET" && endpoint.startsWith("design/")) {
        log(`[DataProvider] GET design — Authorization: ${headers.Authorization ? "present" : "missing"}`);
      }
      const startTime = Date.now();
      const response = await fetch(url, config);
      const duration = Date.now() - startTime;
      
      // Clear timeout since request completed
      clearTimeout(timeoutId);
      
      log(`[DataProvider] Response received in ${duration}ms - Status: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unable to read error response');
        log(`[DataProvider] Error response body: ${errorText}`);
        
        // Detect 401 Unauthorized - try to refresh token and retry
        if (response.status === 401) {
          log(`[DataProvider] ⚠️ 401 Unauthorized - Attempting token refresh and retry`);
          
          const eventHub = this.experience?.eventHub;
          if (eventHub) {
            try {
              // Request token refresh via event
              const newToken = await new Promise((resolve) => {
                const timeout = setTimeout(() => {
                  log("[DataProvider] Token refresh request timeout");
                  eventHub.off("tokenRefreshResponse", tokenHandler);
                  resolve(null);
                }, 10000); // 10 second timeout

                const tokenHandler = (token) => {
                  clearTimeout(timeout);
                  eventHub.off("tokenRefreshResponse", tokenHandler);
                  resolve(token || null);
                };

                eventHub.once("tokenRefreshResponse", tokenHandler);
                eventHub.emit("tokenRefreshRequest");
              });
              
              if (newToken) {
                log(`[DataProvider] ✅ Token refreshed, retrying request`);
                
                // Update headers with new token
                const retryHeaders = {
                  ...headers,
                  Authorization: `Bearer ${newToken}`,
                };
                
                // Retry the request with new token
                const retryResponse = await fetch(url, {
                  ...config,
                  headers: retryHeaders,
                });
                
                if (retryResponse.ok) {
                  log(`[DataProvider] ✅ Retry successful after token refresh`);
                  const retryJsonData = await retryResponse.json();
                  return retryJsonData ?? null;
                } else {
                  log(`[DataProvider] ⚠️ Retry failed with status ${retryResponse.status}`);
                  // If retry still fails, treat as session expired
                  if (retryResponse.status === 401) {
                    eventHub.emit("sessionExpired");
                  }
                  const retryErrorText = await retryResponse.text().catch(() => 'Unable to read error response');
                  throw new Error(`HTTP error! Status: ${retryResponse.status} - ${retryErrorText}`);
                }
              } else {
                log(`[DataProvider] ⚠️ Token refresh failed, session expired`);
                // Token refresh failed, session is truly expired
                eventHub.emit("sessionExpired");
                throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
              }
            } catch (refreshError) {
              log(`[DataProvider] ⚠️ Error during token refresh:`, refreshError);
              // If refresh itself fails, treat as session expired
              eventHub.emit("sessionExpired");
              throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
            }
          } else {
            // No eventHub available, just throw error
            throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
          }
        }
        
        throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
      }

      const jsonData = await response.json();
      log(`[DataProvider] Response data received successfully`);
      return jsonData ?? null;
    } catch (error) {
      // Clear timeout in case of error
      clearTimeout(timeoutId);
      
      log(`[DataProvider] Fetch error for ${url}:`, error);
      console.error("Fetching error:", error);
      
      // Provide more helpful error messages
      if (error.name === 'AbortError' || error.message?.includes('aborted')) {
        const timeout = customTimeout !== null ? customTimeout : this.timeout;
        throw new Error(
          `Request timed out after ${timeout}ms. The server may be slow or the design is very large. Please try again.`
        );
      }
      
      throw error;
    }
  }

  /**
   * Private method to create a timeout signal for fetch requests.
   * @deprecated This method is no longer used - timeout is handled directly in fetch()
   * @param {number} timeoutMs - Timeout in milliseconds (uses default if not provided).
   * @returns {AbortSignal} The abort signal to cancel the request.
   */
  _getTimeoutSignal(timeoutMs = null) {
    const controller = new AbortController();
    const timeout = timeoutMs !== null ? timeoutMs : this.timeout;
    setTimeout(() => {
      log(`[DataProvider] Request timeout after ${timeout}ms`);
      controller.abort();
    }, timeout);
    return controller.signal;
  }

  /**
   * Fetches a list of items from a specified resource.
   * @param {string} resource - The resource to fetch from.
   * @returns {Promise<Array>} An array of fetched items.
   */
  /*   async getList(resource) {
    const endpoint = `${resource}?filter={}&range=[0,${
      this.limit - 1
    }]&sort=["id","ASC"]`;
    return this.fetch(endpoint);
  } */

  async getList(resourceOrOptions) {
    // Check if the argument is an object (new style) or a string (old style)
    if (typeof resourceOrOptions === "string") {
      // Old style: single resource argument
      const resource = resourceOrOptions;
      const endpoint = `${resource}?filter={}&range=[0,${
        this.limit - 1
      }]&sort=["id","ASC"]`;
      return this.fetch(endpoint);
    } else if (typeof resourceOrOptions === "object") {
      // New style: object with multiple options
      const {
        resource,
        filter = {},
        range = `[0,${this.limit - 1}]`,
        sort = `["id","ASC"]`,
      } = resourceOrOptions;

      const endpoint = `${resource}?filter=${JSON.stringify(
        filter
      )}&range=${range}&sort=${sort}`;
      return this.fetch(endpoint);
    } else {
      throw new Error(
        "Invalid argument: must be a resource string or an options object"
      );
    }
  }

  /**
   * Fetches a single item by its ID from a specified resource.
   * @param {string} resource - The resource to fetch from.
   * @param {number|string} id - The ID of the item to fetch.
   * @returns {Promise<Object>} The fetched item.
   */
  async getOne(resource, id) {
    const encodedId = encodeURIComponent(String(id));
    return this.fetch(`${resource}/${encodedId}`);
  }

  /**
   * Fetches a locked design by ID (View Design link from RFQ email).
   * GET /api/v1/locked/design/:designId — requires Authorization: Bearer <JWT>.
   * Backend allows only creator or Admin group.
   * @param {string} designId - The design UUID.
   * @returns {Promise<Object>} The locked design (e.g. designId, data, name, customerId).
   */
  async getLockedDesign(designId) {
    return this.fetch(`locked/design/${encodeURIComponent(designId)}`);
  }

  /**
   * Creates a new item in a specified resource.
   * @param {string} resource - The resource where the item will be created.
   * @param {Object} data - The data of the item to create.
   * @param {number} customTimeout - Optional custom timeout in milliseconds (default: 30000 for saves).
   * @returns {Promise<Object>} The created item.
   */
  async create(resource, data, customTimeout = 30000) {
    return this.fetch(resource, "POST", data, customTimeout);
  }

  /**
   * Updates an item by its ID in a specified resource.
   * @param {string} resource - The resource to update.
   * @param {number|string} id - The ID of the item to update.
   * @param {Object} data - The updated data for the item.
   * @param {number} customTimeout - Optional custom timeout in milliseconds (default: 30000 for saves).
   * @returns {Promise<Object>} The updated item.
   */
  async update(resource, id, data, customTimeout = 30000) {
    return this.fetch(`${resource}/${id}`, "PUT", data, customTimeout);
  }

  /**
   * Deletes an item by its ID from a specified resource.
   * @param {string} resource - The resource to delete from.
   * @param {number|string} id - The ID of the item to delete.
   * @returns {Promise<Object>} Confirmation of the deletion.
   */
  async delete(resource, id) {
    return this.fetch(`${resource}/${id}`, "DELETE");
  }

  async getPresignedUrlsForParts(partIds, customTimeout = 15000) {
  const loginManager = this.experience?.LoginManager || this.experience?.loginManager;
  const token = loginManager ? await loginManager.getToken() : null;
  const controller = new AbortController();
  setTimeout(() => controller.abort(), customTimeout);

  try {
    const response = await fetch(`${this.baseUrl}/generate/presigned/glbs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(partIds),
      signal: controller.signal,
    });

    if (!response.ok) {
      // Handle 401 Unauthorized - try to refresh token and retry
      if (response.status === 401) {
        const eventHub = this.experience?.eventHub;
        if (eventHub) {
          try {
            // Request token refresh via event
            const newToken = await new Promise((resolve) => {
              const timeout = setTimeout(() => {
                log("[DataProvider] Token refresh request timeout for presigned URLs");
                eventHub.off("tokenRefreshResponse", tokenHandler);
                resolve(null);
              }, 10000); // 10 second timeout

              const tokenHandler = (token) => {
                clearTimeout(timeout);
                eventHub.off("tokenRefreshResponse", tokenHandler);
                resolve(token || null);
              };

              eventHub.once("tokenRefreshResponse", tokenHandler);
              eventHub.emit("tokenRefreshRequest");
            });
            
            if (newToken) {
              log(`[DataProvider] ✅ Token refreshed for presigned URLs, retrying request`);
              
              // Retry the request with new token
              const retryResponse = await fetch(`${this.baseUrl}/generate/presigned/glbs`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${newToken}`,
                },
                body: JSON.stringify(partIds),
                signal: controller.signal,
              });
              
              if (retryResponse.ok) {
                log(`[DataProvider] ✅ Retry successful after token refresh for presigned URLs`);
                return await retryResponse.json();
              } else {
                log(`[DataProvider] ⚠️ Retry failed with status ${retryResponse.status}`);
                // If retry still fails, treat as session expired
                if (retryResponse.status === 401) {
                  eventHub.emit("sessionExpired");
                }
                throw new Error(`HTTP error! Status: ${retryResponse.status}`);
              }
            } else {
              log(`[DataProvider] ⚠️ Token refresh failed for presigned URLs, session expired`);
              // Token refresh failed, session is truly expired
              eventHub.emit("sessionExpired");
              throw new Error(`HTTP error! Status: ${response.status}`);
            }
          } catch (refreshError) {
            log(`[DataProvider] ⚠️ Error during token refresh for presigned URLs:`, refreshError);
            // If refresh itself fails, treat as session expired
            eventHub.emit("sessionExpired");
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
        } else {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
      } else {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    }

    return await response.json();
  } catch (error) {
    console.error("Failed to fetch presigned URLs:", error);
    throw error;
  }
}


   async _getAuthHeaders() {
  log("[DataProvider] Emitting tokenRequest...");
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      log("[DataProvider] Token request timeout - no response received");
      this.eventHub.off("tokenResponse", tokenHandler);
      reject(new Error("Token request timeout - user may not be authenticated"));
    }, 10000); // 10 second timeout

    const tokenHandler = (token) => {
      clearTimeout(timeout);
      log("[DataProvider] Received token:", token ? "Token present" : "No token");
      
      if (!token) {
        log("[DataProvider] Warning: No token received, request may fail");
      }
      
      resolve({
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      });
    };

    this.eventHub.once("tokenResponse", tokenHandler);
    this.eventHub.emit("tokenRequest");
  });
}

  
  
}

export { DataProvider };